/**
 * © 2025 Malvys Exaucé Dessou — ZonePro. Tous droits réservés.
 * 
 * Logo ZonePro officiel - Z composé de blocs parcellaires avec marqueur de localisation
 */
import React from 'react';

// Logo variants: 'color', 'mono', 'white'
const ZoneProLogo = ({ variant = 'color', size = 'md', showText = true, className = '' }) => {
  const sizes = {
    sm: { icon: 28, text: 'text-lg', gap: 'gap-1.5' },
    md: { icon: 38, text: 'text-xl', gap: 'gap-2' },
    lg: { icon: 52, text: 'text-2xl', gap: 'gap-2.5' },
    xl: { icon: 68, text: 'text-3xl', gap: 'gap-3' },
  };

  const { icon: iconSize, text: textSize, gap } = sizes[size] || sizes.md;

  const colors = {
    color: {
      fill: '#1A3A5C',
      text: '#1A3A5C',
    },
    mono: {
      fill: '#1A3A5C',
      text: '#1A3A5C',
    },
    white: {
      fill: '#FFFFFF',
      text: '#FFFFFF',
    },
  };

  const { fill, text: textColor } = colors[variant] || colors.color;

  return (
    <div className={`flex items-center ${gap} ${className}`}>
      <svg
        width={iconSize}
        height={iconSize}
        viewBox="0 0 500 500"
        xmlns="http://www.w3.org/2000/svg"
        className="flex-shrink-0"
      >
        <g transform="translate(0,500) scale(0.1,-0.1)" fill={fill} stroke="none">
          <path d="M956 4988 c-53 -14 -121 -84 -136 -138 -6 -23 -8 -60 -5 -81 7 -40 81 -197 130 -276 l26 -43 -95 0 -96 0 0 -380 0 -380 380 0 380 0 0 380 0 380 -241 0 -240 0 26 43 c47 76 125 241 131 277 22 137 -120 256 -260 218z m107 -89 c37 -19 57 -52 57 -94 0 -50 -22 -83 -67 -102 -71 -29 -143 23 -143 102 0 37 5 48 35 74 38 33 79 40 118 20z"/>
          <path d="M1600 4070 l0 -380 380 0 380 0 0 380 0 380 -380 0 -380 0 0 -380z"/>
          <path d="M2410 4070 l0 -380 380 0 380 0 0 380 0 380 -380 0 -380 0 0 -380z"/>
          <path d="M3230 4070 l0 -379 129 -3 130 -3 -210 -184 c-183 -162 -207 -186 -196 -200 98 -115 482 -547 488 -549 5 -2 87 65 183 150 96 84 267 234 380 332 l206 180 0 518 0 518 -555 0 -555 0 0 -380z"/>
          <path d="M2740 3035 c-151 -134 -275 -248 -275 -254 0 -13 483 -561 495 -561 11 0 557 481 561 494 3 11 -483 565 -496 565 -5 0 -134 -110 -285 -244z"/>
          <path d="M2143 2506 c-371 -330 -766 -679 -1088 -962 -148 -130 -272 -242 -275 -248 -3 -6 -4 -188 -2 -404 l4 -392 769 0 769 0 0 380 0 380 -210 0 c-115 0 -210 3 -210 6 0 4 21 25 47 48 26 22 129 113 228 201 100 88 308 272 463 408 155 137 282 250 282 253 0 4 -452 519 -484 550 l-21 21 -272 -241z"/>
          <path d="M3388 1263 l-188 -3 0 -380 0 -380 570 0 570 0 0 385 0 385 -382 -2 c-211 -2 -467 -4 -570 -5z"/>
          <path d="M2380 880 l0 -380 380 0 380 0 0 380 0 380 -380 0 -380 0 0 -380z"/>
        </g>
      </svg>
      
      {showText && (
        <span
          className={`font-bold tracking-tight ${textSize}`}
          style={{ color: textColor }}
        >
          ZonePro
        </span>
      )}
    </div>
  );
};

// Logo icon only (for favicon, small spaces)
export const ZoneProIcon = ({ variant = 'color', size = 32, className = '' }) => {
  const colors = {
    color: { fill: '#1A3A5C' },
    mono: { fill: '#1A3A5C' },
    white: { fill: '#FFFFFF' },
  };

  const { fill } = colors[variant] || colors.color;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 500 500"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <g transform="translate(0,500) scale(0.1,-0.1)" fill={fill} stroke="none">
        <path d="M956 4988 c-53 -14 -121 -84 -136 -138 -6 -23 -8 -60 -5 -81 7 -40 81 -197 130 -276 l26 -43 -95 0 -96 0 0 -380 0 -380 380 0 380 0 0 380 0 380 -241 0 -240 0 26 43 c47 76 125 241 131 277 22 137 -120 256 -260 218z m107 -89 c37 -19 57 -52 57 -94 0 -50 -22 -83 -67 -102 -71 -29 -143 23 -143 102 0 37 5 48 35 74 38 33 79 40 118 20z"/>
        <path d="M1600 4070 l0 -380 380 0 380 0 0 380 0 380 -380 0 -380 0 0 -380z"/>
        <path d="M2410 4070 l0 -380 380 0 380 0 0 380 0 380 -380 0 -380 0 0 -380z"/>
        <path d="M3230 4070 l0 -379 129 -3 130 -3 -210 -184 c-183 -162 -207 -186 -196 -200 98 -115 482 -547 488 -549 5 -2 87 65 183 150 96 84 267 234 380 332 l206 180 0 518 0 518 -555 0 -555 0 0 -380z"/>
        <path d="M2740 3035 c-151 -134 -275 -248 -275 -254 0 -13 483 -561 495 -561 11 0 557 481 561 494 3 11 -483 565 -496 565 -5 0 -134 -110 -285 -244z"/>
        <path d="M2143 2506 c-371 -330 -766 -679 -1088 -962 -148 -130 -272 -242 -275 -248 -3 -6 -4 -188 -2 -404 l4 -392 769 0 769 0 0 380 0 380 -210 0 c-115 0 -210 3 -210 6 0 4 21 25 47 48 26 22 129 113 228 201 100 88 308 272 463 408 155 137 282 250 282 253 0 4 -452 519 -484 550 l-21 21 -272 -241z"/>
        <path d="M3388 1263 l-188 -3 0 -380 0 -380 570 0 570 0 0 385 0 385 -382 -2 c-211 -2 -467 -4 -570 -5z"/>
        <path d="M2380 880 l0 -380 380 0 380 0 0 380 0 380 -380 0 -380 0 0 -380z"/>
      </g>
    </svg>
  );
};

export default ZoneProLogo;
